import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import axios from '../utils/api';
import { toast } from 'react-toastify';

const UserManagement = ({ onBack }) => {
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get('/api/auth/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar usuários');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/auth/admin/users', userForm);
      toast.success('Usuário criado com sucesso!');
      setShowUserModal(false);
      setUserForm({ email: '', password: '', firstName: '', lastName: '' });
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao criar usuário');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await axios.delete(`/api/auth/admin/users/${userId}`);
        toast.success('Usuário excluído com sucesso!');
        loadUsers();
      } catch (error) {
        toast.error('Erro ao excluir usuário');
      }
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/auth/admin/users/${selectedUser.id}/password`, {
        newPassword: newPassword
      });
      toast.success('Senha atualizada com sucesso!');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Erro ao atualizar senha');
    }
  };

  const openUserModal = () => {
    setUserForm({ email: '', password: '', firstName: '', lastName: '' });
    setShowUserModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const styles = {
    container: {
      backgroundColor: '#f1f5f9',
      minHeight: '100vh',
      padding: '1rem'
    },
    card: {
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    cardHeader: {
      backgroundColor: '#00B2A9',
      color: 'white',
      borderRadius: '12px 12px 0 0',
      padding: '1.5rem'
    },
    cardBody: {
      backgroundColor: 'white',
      color: '#64748b'
    },
    primaryButton: {
      backgroundColor: '#00B2A9',
      borderColor: '#00B2A9',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#002F6C',
      borderColor: '#002F6C',
      color: 'white'
    },
    lightButton: {
      backgroundColor: 'white',
      borderColor: '#00B2A9',
      color: '#00B2A9'
    },
    warningButton: {
      backgroundColor: '#f59e0b',
      borderColor: '#f59e0b',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      borderColor: '#ef4444',
      color: 'white'
    },
    table: {
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    tableHeader: {
      backgroundColor: '#f8fafc',
      color: '#002F6C',
      fontWeight: '600'
    },
    textMuted: {
      color: '#94a3b8'
    },
    textPrimary: {
      color: '#002F6C'
    },
    spinner: {
      color: '#00B2A9'
    },
    modal: {
      color: '#64748b'
    },
    modalHeader: {
      backgroundColor: '#00B2A9',
      color: 'white',
      borderRadius: '8px 8px 0 0'
    },
    alert: {
      backgroundColor: '#e0f2fe',
      borderColor: '#00B2A9',
      color: '#002F6C'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Container className="py-5">
          <div className="text-center">
            <div className="spinner-border" role="status" style={styles.spinner}>
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>
        {`
          .btn-light-custom:hover {
            background-color: #00B2A9 !important;
            color: white !important;
            border-color: #00B2A9 !important;
          }
        `}
      </style>
      <Container fluid className="py-4">
        <Row>
          <Col>
            <Card style={styles.card}>
              <Card.Header style={styles.cardHeader} className="d-flex justify-content-between align-items-center">
                <div>
                  <h3 className="mb-0">Gestão de Usuários</h3>
                  <small>Gerenciar usuários do sistema</small>
                </div>
                <Button 
                  style={{
                    ...styles.lightButton,
                    transition: 'all 0.2s ease'
                  }}
                  size="sm" 
                  onClick={onBack}
                  className="btn-light-custom"
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Voltar ao Painel
                </Button>
              </Card.Header>
              <Card.Body style={styles.cardBody}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 style={styles.textPrimary}>Usuários Cadastrados</h4>
                    <p style={styles.textMuted} className="mb-0">Total: {users.length} usuários</p>
                  </div>
                  <Button 
                    style={styles.primaryButton}
                    onClick={openUserModal}
                    onMouseOver={(e) => e.target.style.opacity = '0.9'}
                    onMouseOut={(e) => e.target.style.opacity = '1'}
                  >
                    <i className="bi bi-person-plus me-2"></i>
                    Adicionar Novo Usuário
                  </Button>
                </div>
                
                <div style={styles.table}>
                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr style={styles.tableHeader}>
                        <th>ID</th>
                        <th>Nome</th>
                        <th>E-mail</th>
                        <th>Data de Criação</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(user => (
                        <tr key={user.id}>
                          <td>{user.id}</td>
                          <td>{user.firstName} {user.lastName}</td>
                          <td>{user.email}</td>
                          <td>{new Date(user.createdAt).toLocaleDateString('pt-BR')}</td>
                          <td>
                            <Button 
                              style={styles.warningButton}
                              size="sm" 
                              className="me-2"
                              onClick={() => openPasswordModal(user)}
                              onMouseOver={(e) => e.target.style.opacity = '0.9'}
                              onMouseOut={(e) => e.target.style.opacity = '1'}
                            >
                              <i className="bi bi-key me-1"></i>
                              Alterar Senha
                            </Button>
                            <Button 
                              style={styles.dangerButton}
                              size="sm"
                              onClick={() => handleDeleteUser(user.id)}
                              onMouseOver={(e) => e.target.style.opacity = '0.9'}
                              onMouseOut={(e) => e.target.style.opacity = '1'}
                            >
                              <i className="bi bi-trash me-1"></i>
                              Excluir
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {users.length === 0 && (
                  <div className="text-center py-5">
                    <i className="bi bi-people" style={{fontSize: '4rem', color: '#94a3b8'}}></i>
                    <h5 className="mt-3" style={styles.textMuted}>Nenhum usuário encontrado</h5>
                    <p style={styles.textMuted}>Clique em "Adicionar Novo Usuário" para começar</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal para criar usuário */}
        <Modal show={showUserModal} onHide={() => setShowUserModal(false)}>
          <Modal.Header closeButton style={styles.modalHeader}>
            <Modal.Title>Adicionar Novo Usuário</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleCreateUser}>
            <Modal.Body style={styles.modal}>
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>E-mail</Form.Label>
                <Form.Control
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  required
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Senha</Form.Label>
                <Form.Control
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                  required
                  minLength={6}
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Nome</Form.Label>
                <Form.Control
                  type="text"
                  value={userForm.firstName}
                  onChange={(e) => setUserForm({...userForm, firstName: e.target.value})}
                  required
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Sobrenome</Form.Label>
                <Form.Control
                  type="text"
                  value={userForm.lastName}
                  onChange={(e) => setUserForm({...userForm, lastName: e.target.value})}
                  required
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                style={{backgroundColor: '#94a3b8', borderColor: '#94a3b8'}}
                onClick={() => setShowUserModal(false)}
              >
                Cancelar
              </Button>
              <Button 
                style={styles.primaryButton}
                type="submit"
              >
                Criar Usuário
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Modal para alterar senha */}
        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
          <Modal.Header closeButton style={styles.modalHeader}>
            <Modal.Title>Alterar Senha</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdatePassword}>
            <Modal.Body style={styles.modal}>
              <Alert style={styles.alert}>
                Alterando senha do usuário: <strong>{selectedUser?.email}</strong>
              </Alert>
              <Form.Group className="mb-3">
                <Form.Label style={{color: '#002F6C', fontWeight: '500'}}>Nova Senha</Form.Label>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{borderColor: '#d1d5db', color: '#64748b'}}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button 
                style={{backgroundColor: '#94a3b8', borderColor: '#94a3b8'}}
                onClick={() => setShowPasswordModal(false)}
              >
                Cancelar
              </Button>
              <Button 
                style={styles.primaryButton}
                type="submit"
              >
                Atualizar Senha
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default UserManagement;